import React, { useEffect, useState } from "react";
import {
  Receipt,
  HelpCircle,
  Calendar,
  DollarSign,
  FileText,
  CheckCircle,
  Clock,
  AlertCircle,
  ExternalLink,
  User,
  CreditCard,
} from "lucide-react";
import { useAuth } from "../../contexts/AuthContext";
import RaiseInvoiceModal from "./RaiseInvoiceModal";
import InvoiceDetailsModal from "./InvoiceDetailsModal";
import { TrainerAPI } from "../../api/TrainerAPI";

const AllInvoices = () => {
  const { user } = useAuth();
  const [invoices, setInvoices] = useState([]);
  const [showRaiseInvoiceModal, setShowRaiseInvoiceModal] = useState(false);
  const [showInvoiceDetailsModal, setShowInvoiceDetailsModal] = useState(false);
  const [selectedInvoice, setSelectedInvoice] = useState(null);
  const [loading, setLoading] = useState(true);

  const [batches, setBatches] = useState([]);
  const [currentPage, setCurrentPage] = useState(1);
  const itemsPerPage = 20;

  // Function to sort invoices by invoice number in descending order (latest first)
  const sortInvoicesByNumber = (invoicesArray) => {
    return invoicesArray.sort((a, b) => {
      // Extract numeric part from invoice number for proper sorting
      const getNumericPart = (invNo) => {
        const match = invNo.match(/\d+/);
        return match ? parseInt(match[0]) : 0;
      };

      return getNumericPart(b.inv_no) - getNumericPart(a.inv_no);
    });
  };

  useEffect(() => {
    const fetchData = async () => {
      try {
        // Fetch invoices
        const invoicesList = await TrainerAPI.getTrainerInvoices();
        const sortedInvoices = sortInvoicesByNumber(invoicesList || []);
        setInvoices(sortedInvoices);

        // Fetch batches — current AND previous. A trainer reassigned off a
        // batch still needs to raise an invoice for the classes they taught
        // before the handoff, so limiting this to current batches only
        // would make that batch impossible to select here. Previous batches
        // are fetched best-effort: if that call fails, current batches
        // still populate the picker rather than blocking on it.
        const [currentResult, previousResult] = await Promise.allSettled([
          TrainerAPI.getBatches(),
          TrainerAPI.getPreviousBatches(),
        ]);

        const currentList =
          currentResult.status === "fulfilled" ? currentResult.value || [] : [];
        const previousList =
          previousResult.status === "fulfilled"
            ? previousResult.value || []
            : [];

        if (previousResult.status === "rejected") {
          console.error(
            "Could not load previous batches for invoicing:",
            previousResult.reason,
          );
        }

        setBatches([...currentList, ...previousList]);
      } catch (err) {
        console.error("Error fetching data:", err);
      } finally {
        setLoading(false);
      }
    };

    if (user?.email) {
      fetchData();
    }
  }, [user]);

  const handleRaiseInvoiceClick = () => {
    setShowRaiseInvoiceModal(true);
  };

  const handleInvoiceNumberClick = (invoice) => {
    setSelectedInvoice(invoice);
    setShowInvoiceDetailsModal(true);
  };

  const refreshInvoices = async () => {
    try {
      const invoicesList = await TrainerAPI.getTrainerInvoices();
      const sortedInvoices = sortInvoicesByNumber(invoicesList || []);
      setInvoices(sortedInvoices);
    } catch (err) {
      console.error("Error refreshing invoices:", err);
    }
  };

  // Pagination calculations
  const totalPages = Math.ceil(invoices.length / itemsPerPage);
  const startIndex = (currentPage - 1) * itemsPerPage;
  const endIndex = startIndex + itemsPerPage;
  const currentInvoices = invoices.slice(startIndex, endIndex);

  const handlePageChange = (pageNumber) => {
    setCurrentPage(pageNumber);
  };

  const handlePrevious = () => {
    if (currentPage > 1) {
      setCurrentPage(currentPage - 1);
    }
  };

  const handleNext = () => {
    if (currentPage < totalPages) {
      setCurrentPage(currentPage + 1);
    }
  };

  const getStatusColor = (status) => {
    switch (status?.toLowerCase()) {
      case "submitted":
        return "bg-yellow-100 text-yellow-800";
      case "approved":
        return "bg-green-100 text-green-700";
      case "hold":
        return "bg-red-100 text-red-600";
      case "receipt sent":
        return "bg-blue-100 text-blue-600";
      case "processing":
        return "bg-indigo-100 text-indigo-600";
      case "payment sent":
        return "bg-green-200 text-green-800";
      case "open":
        return "bg-gray-100 text-gray-700";
      default:
        return "bg-gray-100 text-gray-700";
    }
  };

  const getStatusIcon = (status) => {
    switch (status?.toLowerCase()) {
      case "submitted":
        return <Clock className="w-4 h-4" />;
      case "approved":
        return <CheckCircle className="w-4 h-4" />;
      case "hold":
        return <AlertCircle className="w-4 h-4" />;
      case "receipt sent":
      case "payment sent":
        return <Receipt className="w-4 h-4" />;
      case "processing":
        return <FileText className="w-4 h-4" />;
      default:
        return <DollarSign className="w-4 h-4" />;
    }
  };

  // Mobile Invoice Card Component
  const MobileInvoiceCard = ({ invoice, index }) => (
    <div
      key={index}
      className="bg-white border border-gray-200 rounded-lg p-4 mb-4 shadow-sm hover:shadow-md transition-shadow"
    >
      {/* Header with Invoice Number and Status */}
      <div className="flex items-center justify-between mb-3">
        <button
          onClick={() => handleInvoiceNumberClick(invoice)}
          className="text-[#FC7A69] hover:text-[#221E45] font-bold text-lg hover:underline transition-colors"
        >
          {invoice.inv_no}
        </button>

        <div className="flex items-center gap-1">
          {getStatusIcon(invoice.status)}
          <span
            className={`px-2 py-1 rounded-full text-xs font-medium capitalize ${getStatusColor(
              invoice.status,
            )}`}
          >
            {invoice.status}
          </span>
        </div>
      </div>

      {/* Course Name */}
      <h3 className="font-semibold text-gray-900 text-base mb-3 line-clamp-2">
        {invoice.courseName}
      </h3>

      {/* Date and Batch Number */}
      <div className="grid grid-cols-2 gap-4 mb-4">
        <div className="flex items-center">
          <Calendar className="w-4 h-4 text-gray-500 mr-2" />
          <div>
            <div className="text-xs text-gray-500">Date</div>
            <div className="font-semibold text-sm">
              {invoice.date
                ? new Date(invoice.date).toLocaleDateString("en-US", {
                    month: "short",
                    day: "numeric",
                    year: "2-digit",
                  })
                : "N/A"}
            </div>
          </div>
        </div>

        <div className="flex items-center">
          <FileText className="w-4 h-4 text-gray-500 mr-2" />
          <div>
            <div className="text-xs text-gray-500">Batch</div>
            <div className="font-semibold text-sm">
              <span className="px-2 py-1 bg-blue-100 text-blue-800 rounded text-xs">
                {invoice.batchNumber1}
              </span>
            </div>
          </div>
        </div>
      </div>

      {/* Amount Information */}
      <div className="grid grid-cols-2 gap-4 mb-4">
        <div className="flex items-center">
          <DollarSign className="w-4 h-4 text-gray-500 mr-2" />
          <div>
            <div className="text-xs text-gray-500">Amount</div>
            <div className="font-bold text-sm text-green-600">
              ₹{invoice.trainerAskedPayment || 0}
            </div>
          </div>
        </div>

        <div className="flex items-center">
          <CreditCard className="w-4 h-4 text-gray-500 mr-2" />
          <div>
            <div className="text-xs text-gray-500">Net Payable</div>
            <div className="font-bold text-sm text-blue-600">
              ₹{invoice.netPayable || 0}
            </div>
          </div>
        </div>
      </div>

      {/* Approved By */}
      {invoice.approvedBy && (
        <div className="flex items-center mb-4">
          <User className="w-4 h-4 text-gray-500 mr-2" />
          <div>
            <span className="text-xs text-gray-500 mr-2">Approved by:</span>
            <span className="font-medium text-sm">{invoice.approvedBy}</span>
          </div>
        </div>
      )}

      {/* Help Button */}
      <div className="flex justify-end">
        <a
          href={`https://wa.me/918882558799?text=${encodeURIComponent(
            `Hello, I am ${invoice.trainerName}. I need help regarding Invoice Number: ${invoice.inv_no} for Batch Number ${invoice.batchNumber1}.`,
          )}`}
          target="_blank"
          rel="noopener noreferrer"
          className="flex items-center gap-2 px-3 py-2 bg-green-100 text-green-700 rounded-lg text-sm font-medium hover:bg-green-200 transition-colors"
        >
          <HelpCircle className="w-4 h-4" />
          <span>Get Help</span>
          <ExternalLink className="w-3 h-3" />
        </a>
      </div>
    </div>
  );

  // Desktop Table Component
  const DesktopTable = () => (
    <div className="overflow-x-auto">
      <table className="w-full text-xs border border-collapse">
        <thead className="bg-gray-100">
          <tr>
            <th className="p-3 border text-left font-semibold">
              Invoice Number
            </th>
            <th className="p-3 border text-left font-semibold">Date</th>
            <th className="p-3 border text-left font-semibold">Course Name</th>
            <th className="p-3 border text-left font-semibold">
              Batch Numbers
            </th>{" "}
            <th className="p-3 border text-left font-semibold">Amount</th>
            <th className="p-3 border text-left font-semibold">Status</th>
            <th className="p-3 border text-left font-semibold">Approved By</th>
            <th className="p-3 border text-left font-semibold">Net Payable</th>
            <th className="p-3 border text-left font-semibold">Help</th>
          </tr>
        </thead>
        <tbody>
          {loading ? (
            <tr>
              <td colSpan="9" className="p-8 text-center text-gray-500">
                <div className="inline-block animate-spin rounded-full h-8 w-8 border-b-2 border-[#FC7A69] mb-4"></div>
                <div>Loading...</div>
              </td>
            </tr>
          ) : invoices.length === 0 ? (
            <tr>
              <td colSpan="9" className="p-8 text-center text-gray-500">
                <Receipt className="w-16 h-16 text-gray-400 mx-auto mb-4" />
                <div>
                  No invoices found. Click "Raise Invoice" to create your first
                  invoice.
                </div>
              </td>
            </tr>
          ) : (
            currentInvoices.map((inv, idx) => (
              <tr key={idx} className="border-t hover:bg-gray-50">
                <td className="p-3 border font-medium">
                  <button
                    onClick={() => handleInvoiceNumberClick(inv)}
                    className="text-[#FC7A69] hover:text-[#221E45] hover:underline font-semibold transition-colors cursor-pointer"
                    title="Click to view invoice details"
                  >
                    {inv.inv_no}
                  </button>
                </td>
                <td className="p-3 border">
                  {inv.date ? new Date(inv.date).toLocaleDateString() : "N/A"}
                </td>
                <td className="p-3 border">{inv.courseName}</td>
                <td className="p-3 border">
                  <div className="flex flex-wrap gap-1">
                    {[inv.batchNumber1]
                      .filter(Boolean)
                      .map((batchNo, index) => (
                        <span
                          key={index}
                          className="px-2 py-1 bg-blue-100 text-blue-800 rounded text-xs"
                        >
                          {batchNo}
                        </span>
                      ))}
                  </div>
                </td>
                <td className="p-3 border">
                  <span className="font-medium">
                    ₹{inv.trainerAskedPayment || 0}
                  </span>
                </td>
                <td className="p-3 border">
                  <span
                    className={`px-2 py-1 rounded-full text-xs font-medium capitalize ${getStatusColor(
                      inv.status,
                    )}`}
                  >
                    {inv.status}
                  </span>
                </td>
                <td className="p-3 border">
                  <span className="font-medium">{inv.approvedBy || "N/A"}</span>
                </td>
                <td className="p-3 border">
                  <span className="font-medium">₹{inv.netPayable || 0}</span>
                </td>
                <td className="p-3 border">
                  <a
                    href={`https://wa.me/918882558799?text=${encodeURIComponent(
                      `Hello, I am ${inv.trainerName}. I need help regarding Invoice Number: ${inv.inv_no} for Batch Number ${inv.batchNumber1}.`,
                    )}`}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-green-600 hover:underline"
                  >
                    Help
                  </a>
                </td>
              </tr>
            ))
          )}
        </tbody>
      </table>
    </div>
  );

  return (
    <div className="bg-white rounded-xl shadow p-4 lg:p-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4 px-2 lg:px-4 py-4">
        <div>
          <h2 className="text-xs font-semibold text-gray-800">All Invoices</h2>
          {invoices.length > 0 && (
            <p className="text-xs text-gray-600 mt-1">
              Total: {invoices.length} invoices
            </p>
          )}
        </div>
        <button
          className="bg-gradient-to-r from-[#FC7A69] to-[#221E45] text-white px-4 py-2 rounded-lg shadow hover:opacity-90 transition w-full sm:w-auto"
          onClick={handleRaiseInvoiceClick}
        >
          + Raise Invoice
        </button>
      </div>

      {/* Responsive Content */}
      {loading ? (
        <div className="text-center py-12">
          <div className="inline-block animate-spin rounded-full h-8 w-8 border-b-2 border-[#FC7A69]"></div>
          <p className="text-gray-600 mt-4">Loading invoices...</p>
        </div>
      ) : invoices.length === 0 ? (
        <div className="text-center py-12">
          <Receipt className="w-16 h-16 text-gray-400 mx-auto mb-4" />
          <p className="text-gray-600 mb-4">No invoices found</p>
          <button
            onClick={handleRaiseInvoiceClick}
            className="bg-gradient-to-r from-[#FC7A69] to-[#221E45] text-white px-6 py-2 rounded-lg hover:opacity-90 transition"
          >
            Create Your First Invoice
          </button>
        </div>
      ) : (
        <>
          {/* Mobile View */}
          <div className="block lg:hidden px-2">
            {currentInvoices.map((invoice, index) => (
              <MobileInvoiceCard key={index} invoice={invoice} index={index} />
            ))}
          </div>

          {/* Desktop View */}
          <div className="hidden lg:block">
            <DesktopTable />
          </div>
        </>
      )}

      {/* Pagination */}
      {totalPages > 1 && !loading && (
        <div className="flex flex-col sm:flex-row justify-between items-center mt-6 px-2 lg:px-4 gap-4">
          <div className="text-xs text-gray-600">
            Showing {startIndex + 1}-{Math.min(endIndex, invoices.length)} of{" "}
            {invoices.length} invoices
          </div>
          <div className="flex items-center space-x-2">
            <button
              onClick={handlePrevious}
              disabled={currentPage === 1}
              className={`px-3 py-2 rounded border text-xs ${
                currentPage === 1
                  ? "bg-gray-100 text-gray-400 cursor-not-allowed"
                  : "bg-white text-gray-700 hover:bg-gray-50 border-gray-300"
              }`}
            >
              Previous
            </button>

            {/* Page numbers - show fewer on mobile */}
            <div className="hidden sm:flex space-x-1">
              {Array.from({ length: Math.min(5, totalPages) }, (_, i) => {
                let pageNum;
                if (totalPages <= 5) {
                  pageNum = i + 1;
                } else if (currentPage <= 3) {
                  pageNum = i + 1;
                } else if (currentPage >= totalPages - 2) {
                  pageNum = totalPages - 4 + i;
                } else {
                  pageNum = currentPage - 2 + i;
                }

                return (
                  <button
                    key={pageNum}
                    onClick={() => handlePageChange(pageNum)}
                    className={`px-3 py-2 rounded border text-sm ${
                      currentPage === pageNum
                        ? "bg-gradient-to-r from-[#FC7A69] to-[#221E45] text-white"
                        : "bg-white text-gray-700 hover:bg-gray-50 border-gray-300"
                    }`}
                  >
                    {pageNum}
                  </button>
                );
              })}
            </div>

            {/* Mobile page indicator */}
            <div className="sm:hidden px-3 py-2 bg-gray-100 rounded text-xs text-gray-600">
              {currentPage} / {totalPages}
            </div>

            <button
              onClick={handleNext}
              disabled={currentPage === totalPages}
              className={`px-3 py-2 rounded border text-xs ${
                currentPage === totalPages
                  ? "bg-gray-100 text-gray-400 cursor-not-allowed"
                  : "bg-white text-gray-700 hover:bg-gray-50 border-gray-300"
              }`}
            >
              Next
            </button>
          </div>
        </div>
      )}

      {/* Raise Invoice Modal */}
      {showRaiseInvoiceModal && (
        <RaiseInvoiceModal
          isOpen={showRaiseInvoiceModal}
          batches={batches}
          trainerEmail={user?.email}
          onClose={() => {
            setShowRaiseInvoiceModal(false);
          }}
          onSuccess={() => {
            setShowRaiseInvoiceModal(false);
            // alert("Invoice submitted successfully!");
            refreshInvoices(); // Refresh the invoices table
          }}
        />
      )}

      {/* Invoice Details Modal */}
      {showInvoiceDetailsModal && selectedInvoice && (
        <InvoiceDetailsModal
          isOpen={showInvoiceDetailsModal}
          invoice={selectedInvoice}
          onClose={() => {
            setShowInvoiceDetailsModal(false);
            setSelectedInvoice(null);
          }}
        />
      )}
    </div>
  );
};

export default AllInvoices;
